// KycInboxPage.jsx (Option B: Unified Inbox)
// - Single table for: KYC pending review, KYC awaiting submission, Docs pending review, Docs awaiting client
// - Filter chips: Type (All/KYC/Docs) + Status (All/Pending/Awaiting)
// - Keeps existing Firestore listeners + resend invite logic
// - Search applies across everything

import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import StaffDocumentsReviewPage from "./StaffDocumentsReviewPage";
import { hasPendingDocumentReview } from '../utils/documentReviewPolicy';
import KycReviewPage from "./KycReviewPage";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  getDoc,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { db, auth, storage } from "../firebase";
import { appConfirm } from "../components/ui/appConfirm";
import { signOut } from "firebase/auth";
import { AnimatePresence, motion } from "framer-motion";
import { Toast } from "primereact/toast";
import { Tooltip } from "react-tooltip";
import "./css/KycInboxPage.css";
import iconHomeA from "../images/home-A.svg";
import { useStaffTopBand } from "../components/ui/StaffTopBandContext";
import { MAIL_API_BASE } from "../config/api";
import { linkTimeLeftLabel } from "../utils/linkTimeLeft";
import {
  forceReleaseBodyScrollLock,
  useLockBodyScroll,
} from "../kyc/sections/common/common_utils/useLockBodyScroll";

// ✅ single source of truth
import { useAuth } from "../auth/AuthProvider";

// IMPORT COMPONENTS
import SearchBar from "../components/ui/SearchBar";

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

function safeStr(v) {
  return String(v || "").trim();
}

function fmtDate(ts) {
  if (!ts) return "";
  if (typeof ts?.toDate === "function") {
    return ts.toDate().toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  // fallback if it's ISO string or Date
  try {
    const d = typeof ts === "string" ? new Date(ts) : ts;
    if (d instanceof Date && !Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch (e) {}
  return "";
}

// Any past time: makes an awaiting row show "Expired".
const EXPIRED_LINK_MS = 1;

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts?.toMillis === "function") return ts.toMillis();
  if (typeof ts?.toDate === "function") return ts.toDate().getTime();
  if (typeof ts === "string") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (ts instanceof Date) return ts.getTime();
  return 0;
}

function normType(t) {
  const s = String(t || "").toLowerCase();
  if (s === "entity" || s === "company") return "entity";
  if (s === "individual" || s === "person") return "individual";
  return "";
}

function staffAccessQuery(baseRef, { orgId, role, teamId, uid }, ...extraFilters) {
  if (role === "manager") {
    return query(baseRef, where("orgId", "==", orgId), ...extraFilters);
  }

  if (teamId) {
    return query(
      baseRef,
      where("orgId", "==", orgId),
      where("accessScope", "==", "team"),
      where("teamId", "==", teamId),
      ...extraFilters
    );
  }

  return query(
    baseRef,
    where("orgId", "==", orgId),
    where("accessScope", "==", "individual"),
    where("ownerUid", "==", uid),
    ...extraFilters
  );
}

function orgScopedEmailLogsQuery({ isAdmin, orgId, ...filters }) {
  const cleanFilters = Object.entries(filters)
    .filter(([, value]) => String(value ?? "").trim())
    .map(([key, value]) => where(key, "==", value));

  if (isAdmin) {
    return query(collection(db, "email_logs"), ...cleanFilters);
  }

  return query(
    collection(db, "email_logs"),
    where("orgId", "==", orgId),
    ...cleanFilters
  );
}


export default function KycInboxPage() {
  const nav = useNavigate();
  const toastRef = useRef(null);

  // ✅ SINGLE source of truth (no auth/org checks here)
  const { authLoading, isAdmin, me, role } = useAuth();
  const myOrgId = String(me?.orgId || "").trim();
  const myTeamId = String(me?.teamId || "").trim();
  const myUid = String(auth.currentUser?.uid || me?.uid || "").trim();

  // ---------------------------
  // Inbox state
  // ---------------------------
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [inviteRows, setInviteRows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]); // kyc_submissions pending
  const [err, setErr] = useState("");

  const [docPendingReview, setDocPendingReview] = useState([]);
  const [docAwaitingClient, setDocAwaitingClient] = useState([]);

  // Search + filters
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | kyc | docs
  // const [statusFilter, setStatusFilter] = useState("all"); // all | pending | awaiting
  const [statusFilter, setStatusFilter] = useState("pending");

  // Client headers map (for pending submissions table)
  const [clientMap, setClientMap] = useState({});
  const clientMapRef = useRef({});
  const mountedRef = useRef(true);

  const [resendingId, setResendingId] = useState("");
  const [resendingDocId, setResendingDocId] = useState("");

  const [reviewDocId, setReviewDocId] = useState(null);
  const [reviewClientName, setReviewClientName] = useState("");
  const [innerDocModalOpen, setInnerDocModalOpen] = useState(false);

  const [kycReviewSubId, setKycReviewSubId] = useState(null);
  const [kycReviewClientName, setKycReviewClientName] = useState("");

  // ---------------------------
  // Mounted ref
  // ---------------------------
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useLockBodyScroll(!!reviewDocId || !!kycReviewSubId, { strategy: "overflow" });

  useEffect(() => {
    return () => {
      forceReleaseBodyScrollLock();
    };
  }, []);

  // ---------------------------
  // Logout
  // ---------------------------
  async function handleLogout() {
    sessionStorage.setItem("mykyc:loggedOut", "1");
    await signOut(auth);
    nav("/login", { replace: true });
  }

  // ---------------------------
  // Ensure client headers cached (for kyc_submissions rows)
  // ---------------------------
  async function ensureClientHeaders(clientIds) {
    try {
      const unique = Array.from(new Set(clientIds.filter(Boolean)));
      const missing = unique.filter((cid) => !clientMapRef.current[cid]);
      if (missing.length === 0) return;

      const snaps = await Promise.all(
        missing.map((cid) => getDoc(doc(db, "clients", cid)))
      );

      const patch = {};
      snaps.forEach((snap, i) => {
        const cid = missing[i];
        if (snap.exists()) {
          const c = snap.data() || {};
          patch[cid] = {
            name: c.clientName || c.name || cid,
            type: normType(c.clientType || c.type),
            status: (c.status || "").toLowerCase(),
            accessScope: String(c.accessScope || "").trim(),
            ownerUid: String(c.ownerUid || "").trim(),
            teamId: String(c.teamId || "").trim(),
            createdByUid: String(c.createdByUid || "").trim(),
          };
        } else {
          patch[cid] = { name: cid, type: "", status: "" };
        }
      });

      if (!mountedRef.current) return;

      setClientMap((prev) => {
        const next = { ...prev, ...patch };
        clientMapRef.current = next;
        return next;
      });
    } catch (e) {
      console.warn("ensureClientHeaders failed:", e?.message || e);
    }
  }

  // ---------------------------
  // Resend doc upload link
  // ---------------------------
  async function handleResendDocUpload(item) {
    const clientId = item?.clientId;
    if (!clientId) return;
    try {
      setResendingDocId(clientId);
      const token = await auth.currentUser?.getIdToken();
      const resp = await fetch(`${MAIL_API_BASE}/myKyc/staff/doc-upload-link/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ clientId }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json.ok) {
        throw new Error(json?.error || "Failed to resend upload link");
      }
      toastRef.current?.show({
        severity: "success",
        summary: "Email sent",
        detail: json?.to ? `Upload link resent to ${json.to}.` : "Upload link resent to client.",
        life: 3000,
      });
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Send failed",
        detail: e?.message || "Failed to resend upload link",
        life: 3500,
      });
    } finally {
      setResendingDocId("");
    }
  }

  // Resend invite
  // ---------------------------
  async function handleResendInvite(client) {
    const clientId = client?.clientId;
    const email = safeStr(client?.kycInviteEmail);
    if (!clientId) return;

    if (!email) {
      alert("Cannot resend: kycInviteEmail is missing for this client.");
      return;
    }

    try {
      setResendingId(clientId);

      const token = await auth.currentUser?.getIdToken();
      const resp = await fetch(`${MAIL_API_BASE}/myKyc/staff/kyc-link/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ clientId, email }),
      });

      const json = await resp.json();
      if (!resp.ok || !json.ok) {
        throw new Error(json?.error || "Failed to resend invitation");
      }

      toastRef.current?.show({
        severity: "success",
        summary: "Email sent",
        detail: `Invitation link resent to ${email}.`,
        life: 3000,
      });

      await updateDoc(doc(db, "clients", clientId), {
        kycInviteEmail: email,
        kycInviteSentAt: serverTimestamp(),
        kycFlowStage: "sent",
      });
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Send failed",
        detail: e?.message || "Failed to resend invitation",
        life: 3500,
      });
    } finally {
      setResendingId("");
    }
  }

  const deleteTargetRef = useRef(null);

  async function handleDeleteKycRow(item) {
    const { clientId, submissionId, statusGroup } = item;
    const ops = [];

    // Reset KYC flow stage on client (keep the client itself)
    ops.push(
      updateDoc(doc(db, "clients", clientId), {
        kycFlowStage: null,
        kycInviteSentAt: null,
        kycInviteEmail: null,
      })
    );

    // Delete kyc_submission doc if pending
    if (statusGroup === "pending" && submissionId) {
      ops.push(deleteDoc(doc(db, "kyc_submissions", submissionId)));
    }

    // Delete kyc_invites/{clientId} and kyc_client_drafts/{clientId}
    ops.push(deleteDoc(doc(db, "kyc_invites", clientId)).catch(() => {}));
    ops.push(deleteDoc(doc(db, "kyc_client_drafts", clientId)).catch(() => {}));

    // Delete all email_logs for this client
    const emailSnap = await getDocs(
      orgScopedEmailLogsQuery({ isAdmin, orgId: myOrgId, clientId })
    );
    emailSnap.docs.forEach((d) => ops.push(deleteDoc(d.ref)));

    await Promise.all(ops);
  }

  async function handleDeleteDocsRow(item) {
    const { clientId, submissionId } = item;

    // Read documentSubmission to get file paths
    const subSnap = await getDoc(doc(db, "documentSubmissions", submissionId));
    if (subSnap.exists()) {
      const files = subSnap.data().files || [];
      const paths = files.map((f) => f.objectPath).filter(Boolean);
      await Promise.allSettled(
        paths.map((p) => deleteObject(storageRef(storage, p)))
      );
    }

    // Delete documentSubmissions doc
    await deleteDoc(doc(db, "documentSubmissions", submissionId));

    // Delete doc_upload_invites/{clientId}
    await deleteDoc(doc(db, "doc_upload_invites", clientId)).catch(() => {});

    // Delete email_logs of type documents_request for this client
    const emailSnap = await getDocs(
      orgScopedEmailLogsQuery({
        isAdmin,
        orgId: myOrgId,
        clientId,
        type: "documents_request",
      })
    );
    await Promise.all(emailSnap.docs.map((d) => deleteDoc(d.ref)));
  }

  function confirmDeleteRow(item) {
    deleteTargetRef.current = item;
    appConfirm({
      group: "appConfirm",
      header: "Confirm deletion",
      message: `Are you sure you want to delete this ${item.type === "docs" ? "document submission" : "KYC record"} for ${item.clientName}? This cannot be undone.`,
      defaultFocus: "reject",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: async () => {
        const it = deleteTargetRef.current;
        if (!it) return;
        try {
          if (it.type === "docs") {
            await handleDeleteDocsRow(it);
          } else {
            await handleDeleteKycRow(it);
          }
          toastRef.current?.show({
            severity: "success",
            summary: "Deleted",
            detail: `Record for "${it.clientName}" was removed.`,
            life: 2500,
          });
        } catch (err) {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: err?.message || "Delete failed",
            life: 3500,
          });
        } finally {
          deleteTargetRef.current = null;
        }
      },
      reject: () => { deleteTargetRef.current = null; },
    });
  }

  // ---------------------------
  // ✅ Listener: Invites sent but NOT submitted yet
  // ---------------------------
  useEffect(() => {
    if (authLoading) return;

    setInvitesLoading(true);

    if (!isAdmin && (!myOrgId || (role !== "manager" && !myTeamId && !myUid))) {
      setInviteRows([]);
      setInvitesLoading(false);
      return;
    }

    let qInvites;

    if (isAdmin) {
      qInvites = query(
        collection(db, "clients"),
        where("kycFlowStage", "==", "sent"),
        orderBy("kycInviteSentAt", "desc")
      );
    } else {
      qInvites = staffAccessQuery(
        collection(db, "clients"),
        { orgId: myOrgId, role, teamId: myTeamId, uid: myUid },
        where("kycFlowStage", "==", "sent"),
        orderBy("kycInviteSentAt", "desc")
      );
    }

    const unsub = onSnapshot(
      qInvites,
      (snap) => {
        const items = snap.docs.map((d) => ({
          clientId: d.id,
          ...d.data(),
        }));
        setInviteRows(items);
        setInvitesLoading(false);
      },
      (e) => {
        console.error("Invites snapshot failed:", e);
        setInviteRows([]);
        setInvitesLoading(false);
      }
    );

    return () => unsub();
  }, [authLoading, isAdmin, myOrgId, myTeamId, myUid, role]);

  // ---------------------------
  // ✅ Listener: KYC submissions pending review
  // ---------------------------
  useEffect(() => {
    if (authLoading) return;

    setErr("");
    setLoading(true);

    if (!isAdmin && (!myOrgId || (role !== "manager" && !myTeamId && !myUid))) {
      setRows([]);
      setLoading(false);
      return;
    }

    let qSubmissions;

    if (isAdmin) {
      qSubmissions = query(
        collection(db, "kyc_submissions"),
        where("status", "==", "pending"),
        orderBy("submittedAt", "desc")
      );
    } else {
      qSubmissions = staffAccessQuery(
        collection(db, "kyc_submissions"),
        { orgId: myOrgId, role, teamId: myTeamId, uid: myUid },
        where("status", "==", "pending"),
        orderBy("submittedAt", "desc")
      );
    }

    const unsub = onSnapshot(
      qSubmissions,
      async (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(items);

        await ensureClientHeaders(items.map((x) => x.clientId));
        if (mountedRef.current) setLoading(false);
      },
      (e) => {
        setErr(e?.message || "Failed to load inbox");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [authLoading, isAdmin, myOrgId, myTeamId, myUid, role]);

  // ---------------------------
  // ✅ Listener: Documents queues
  // ---------------------------
  useEffect(() => {
    if (authLoading) return;

    if (!isAdmin && (!myOrgId || (role !== "manager" && !myTeamId && !myUid))) {
      setDocPendingReview([]);
      setDocAwaitingClient([]);
      return;
    }

    let qPendingDocs, qAwaitDocs;

    if (isAdmin) {
      qPendingDocs = query(
        collection(db, "documentSubmissions"),
        where("type", "==", "documents"),
        where("status", "==", "pending_review"),
        orderBy("updatedAt", "desc"),
        limit(100)
      );

      qAwaitDocs = query(
        collection(db, "documentSubmissions"),
        where("type", "==", "documents"),
        where("status", "==", "awaiting_client"),
        orderBy("updatedAt", "desc"),
        limit(100)
      );
    } else {
      qPendingDocs = staffAccessQuery(
        collection(db, "documentSubmissions"),
        { orgId: myOrgId, role, teamId: myTeamId, uid: myUid },
        where("type", "==", "documents"),
        where("status", "==", "pending_review"),
        orderBy("updatedAt", "desc"),
        limit(100)
      );

      qAwaitDocs = staffAccessQuery(
        collection(db, "documentSubmissions"),
        { orgId: myOrgId, role, teamId: myTeamId, uid: myUid },
        where("type", "==", "documents"),
        where("status", "==", "awaiting_client"),
        orderBy("updatedAt", "desc"),
        limit(100)
      );
    }

    const unsubPendingDocs = onSnapshot(
      qPendingDocs,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDocPendingReview(list.filter(hasPendingDocumentReview));
        ensureClientHeaders(list.map((x) => x.clientId));
      },
      (err) => console.error("[documentSubmissions pending_review] error:", err)
    );

    const unsubAwaitDocs = onSnapshot(
      qAwaitDocs,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDocAwaitingClient(list);
        ensureClientHeaders(list.map((x) => x.clientId));
      },
      (err) =>
        console.error("[documentSubmissions awaiting_client] error:", err)
    );

    return () => {
      unsubPendingDocs();
      unsubAwaitDocs();
    };
  }, [authLoading, isAdmin, myOrgId, myTeamId, myUid, role]);

  // ---------------------------
  // Derivations
  // ---------------------------

  // Filter out invites that already have a pending submission
  const invitesNotSubmitted = useMemo(() => {
    const pendingClientIds = new Set(
      rows.map((r) => r.clientId).filter(Boolean)
    );
    return inviteRows.filter(
      (c) => c.clientId && !pendingClientIds.has(c.clientId)
    );
  }, [inviteRows, rows]);

  const canSeeClient = (clientId, row = {}) => {
    if (isAdmin || role === "manager") return true;
    const rowScope = String(row?.accessScope || "").trim();
    const rowTeamId = String(row?.teamId || "").trim();
    const rowOwnerUid = String(row?.ownerUid || "").trim();
    if (rowScope === "team") return !!myTeamId && !!rowTeamId && rowTeamId === myTeamId;
    if (rowScope === "individual") return !!rowOwnerUid && rowOwnerUid === myUid;
    const cachedScope = String(clientMap[clientId]?.accessScope || "").trim();
    const cachedTeamId = String(clientMap[clientId]?.teamId || "").trim();
    const cachedOwnerUid = String(clientMap[clientId]?.ownerUid || "").trim();
    if (cachedScope === "team") return !!myTeamId && !!cachedTeamId && cachedTeamId === myTeamId;
    if (cachedScope === "individual") return !!cachedOwnerUid && cachedOwnerUid === myUid;
    return false;
  };

  // Link of awaiting rows (kyc_invites / doc_upload_invites, doc id = clientId): expiry and
  // reminder, followed live so a resend or a reminder shows without reloading.
  const [linkInfo, setLinkInfo] = useState({});
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  const awaitingLinkKeys = useMemo(() => [...new Set([
    ...invitesNotSubmitted.map((c) => safeStr(c.clientId) && `kyc_invites:${safeStr(c.clientId)}`),
    ...docAwaitingClient.map((d) => safeStr(d.clientId) && `doc_upload_invites:${safeStr(d.clientId)}`),
  ].filter(Boolean))].sort().join("|"), [invitesNotSubmitted, docAwaitingClient]);
  useEffect(() => {
    if (!awaitingLinkKeys) return undefined;
    const unsubscribers = awaitingLinkKeys.split("|").map((key) => {
      const [collectionName, clientId] = key.split(":");
      return onSnapshot(
        doc(db, collectionName, clientId),
        (snap) => {
          const data = snap.exists() ? snap.data() || {} : null;
          // No link record: the link can no longer be opened, so it counts as expired.
          const info = data
            ? {
                expiresMs: toMillis(data.expiresAt),
                reminderSentMs: toMillis(data.reminderSentAt),
                secondReminderSentMs: toMillis(data.secondReminderSentAt),
              }
            : { expiresMs: EXPIRED_LINK_MS };
          setLinkInfo((prev) => ({ ...prev, [key]: info }));
        },
        (e) => console.warn(`[inbox] link not readable (${key}):`, e?.message || e)
      );
    });
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [awaitingLinkKeys]);

  // counts (for UI badges)
  const counts = useMemo(() => {
    const kycPending = rows.length;
    const kycAwaiting = invitesNotSubmitted.length;
    const docsPending = docPendingReview.length;
    const docsAwaiting = docAwaitingClient.length;

    return {
      kycPending,
      kycAwaiting,
      docsPending,
      docsAwaiting,
      totalPending: kycPending + docsPending,
      totalAwaiting: kycAwaiting + docsAwaiting,
      total: kycPending + kycAwaiting + docsPending + docsAwaiting,
    };
  }, [rows, invitesNotSubmitted, docPendingReview, docAwaitingClient]);

  // unify all items into one list
  const unifiedItems = useMemo(() => {
    const out = [];

    // 1) KYC pending review (kyc_submissions)
    for (const r of rows) {
      const c = clientMap[r.clientId] || {};
      if (!canSeeClient(r.clientId, r)) continue;
      out.push({
        _key: `kyc_pending_${r.id}`,
        type: "kyc",
        statusGroup: "pending",
        statusLabel: "Pending review",
        clientId: safeStr(r.clientId),
        submissionId: r.id,
        clientName: safeStr(c.name) || safeStr(r.clientId) || "—",
        clientType: safeStr(c.type),
        clientStatus: safeStr(c.status),
        dateTs: r.submittedAt,
        dateLabel: "Submitted by client",
        actionLabel: "Review",
        onAction: () => { setKycReviewSubId(r.id); setKycReviewClientName(safeStr(c.name) || safeStr(r.clientId) || ""); },
        rowOnClick: () => { setKycReviewSubId(r.id); setKycReviewClientName(safeStr(c.name) || safeStr(r.clientId) || ""); },
      });
    }

    // 2) KYC awaiting submission (clients invites sent)
    for (const c of invitesNotSubmitted) {
      if (!canSeeClient(c.clientId, c)) continue;
      const type = normType(c.clientType || c.type);
      const status = safeStr(c.status).toLowerCase();

      out.push({
        _key: `kyc_await_${c.clientId}`,
        type: "kyc",
        statusGroup: "awaiting",
        statusLabel: "Awaiting client",
        clientId: safeStr(c.clientId),
        submissionId: null,
        clientName: safeStr(c.clientName || c.name || c.clientId) || "—",
        clientType: type,
        clientStatus: status,
        email: safeStr(c.kycInviteEmail),
        dateTs: c.kycInviteSentAt,
        dateLabel: "Sent to client",
        linkKey: `kyc_invites:${safeStr(c.clientId)}`,
        actionLabel: resendingId === c.clientId ? "Sending…" : "Resend",
        actionDisabled: resendingId === c.clientId,
        onAction: () => handleResendInvite(c),
        rowOnClick: null, // don't navigate on click for awaiting
      });
    }

    // 3) Docs pending review
    for (const d of docPendingReview) {
      const c = clientMap[d.clientId] || {};
      if (!canSeeClient(d.clientId, d)) continue;
      out.push({
        _key: `doc_pending_${d.id}`,
        type: "docs",
        statusGroup: "pending",
        statusLabel: "Pending review",
        clientId: safeStr(d.clientId),
        submissionId: d.id,
        clientName: safeStr(d.clientName || d.clientId || d.id) || "—",
        clientType: "",
        clientStatus: safeStr(c.status),
        dateTs: d.updatedAt,
        dateLabel: "Submitted by client",
        actionLabel: "Review",
        onAction: () => { setReviewDocId(d.id); setReviewClientName(safeStr(d.clientName || d.clientId || d.id)); },
        rowOnClick: () => { setReviewDocId(d.id); setReviewClientName(safeStr(d.clientName || d.clientId || d.id)); },
      });
    }

    // 4) Docs awaiting client
    for (const d of docAwaitingClient) {
      const c = clientMap[d.clientId] || {};
      if (!canSeeClient(d.clientId, d)) continue;
      out.push({
        _key: `doc_await_${d.id}`,
        type: "docs",
        statusGroup: "awaiting",
        statusLabel: "Awaiting client",
        clientId: safeStr(d.clientId),
        submissionId: d.id,
        clientName: safeStr(d.clientName || d.clientId || d.id) || "—",
        clientType: "",
        clientStatus: safeStr(c.status),
        dateTs: d.updatedAt,
        dateLabel: "Sent to client",
        linkKey: `doc_upload_invites:${safeStr(d.clientId)}`,
        actionLabel: resendingDocId === safeStr(d.clientId) ? "Sending…" : "Resend",
        actionDisabled: resendingDocId === safeStr(d.clientId),
        onAction: () => handleResendDocUpload({ clientId: safeStr(d.clientId) }),
        rowOnClick: null,
      });
    }

    // Sort newest first by date
    out.sort((a, b) => toMillis(b.dateTs) - toMillis(a.dateTs));

    return out;
  }, [
    rows,
    invitesNotSubmitted,
    docPendingReview,
    docAwaitingClient,
    clientMap,
    nav,
    resendingId,
    resendingDocId,
    isAdmin,
    role,
    myTeamId,
  ]);

  // search + filters applied
  const searchQ = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    let list = unifiedItems;

    // type filter
    if (typeFilter === "kyc") list = list.filter((x) => x.type === "kyc");
    if (typeFilter === "docs") list = list.filter((x) => x.type === "docs");

    // status filter
    if (statusFilter === "pending")
      list = list.filter((x) => x.statusGroup === "pending");
    if (statusFilter === "awaiting")
      list = list.filter((x) => x.statusGroup === "awaiting");

    // search
    if (searchQ) {
      list = list.filter((x) => {
        const name = safeStr(x.clientName).toLowerCase();
        const cid = safeStr(x.clientId).toLowerCase();
        const email = safeStr(x.email).toLowerCase();
        return (
          name.includes(searchQ) ||
          cid.includes(searchQ) ||
          email.includes(searchQ)
        );
      });
    }

    return list;
  }, [unifiedItems, typeFilter, statusFilter, searchQ]);

  // ---------------------------
  // UI helpers
  // ---------------------------
  const statusIconClass = (status) =>
    status === "client"
      ? "bi bi-shield-check typeIconClient"
      : status === "prospect"
      ? "bi bi-binoculars typeIconProspect"
      : status === "former client"
      ? "bi bi-archive typeIconFormerClient"
      : "bi bi-question-circle";

  const renderTypeBadge = (item) => {
    if (item.type === "kyc") {
      return (
        <span className="badge badgeType badgeTypeIndividual">
          <i className="bi bi-file-earmark-text me-1" aria-hidden="true" />
          KYC Form
        </span>
      );
    }
    return (
      <span className="badge badgeType badgeTypeIndividual">
        <i className="bi bi-paperclip me-1" aria-hidden="true" />
        Documents
      </span>
    );
  };

  const renderStatusPill = (item) => {
    if (item.statusGroup === "pending") {
      return (
        <span className="badge myBadgePending">
          {item.statusLabel || "Pending review"}
        </span>
      );
    }
    const link = linkInfo[item.linkKey] || {};
    const timeLeft = linkTimeLeftLabel(link.expiresMs, nowMs);
    const sentReminders = [link.reminderSentMs, link.secondReminderSentMs].filter(Boolean);
    return (
      <span className="kycInboxStatusBadges">
        <span className="badge myBadgeAwaiting">
          {timeLeft === "expired"
            ? "Expired"
            : `${item.statusLabel || "Awaiting client"}${timeLeft ? ` · ${timeLeft}` : ""}`}
        </span>
        {sentReminders.length ? (
          <span
            className="badge myBadgeAwaiting"
            data-tooltip-id="inboxReminderTip"
            data-tooltip-content={sentReminders.length === 1
              ? `Reminder sent on ${fmtDate(new Date(sentReminders[0]))}`
              : `1st reminder: ${fmtDate(new Date(sentReminders[0]))} · 2nd reminder: ${fmtDate(new Date(sentReminders[1]))}`}
          >
            {sentReminders.length === 1 ? "Reminder sent" : "2 reminders sent"}
          </span>
        ) : null}
      </span>
    );
  };

  const chipClass = (on) =>
    `btn btn-sm kycInboxFilterChip ${on ? "is-active" : ""}`;

  useStaffTopBand(
    {
      title: "INBOX · Kyc & Documents",
      contentClassName: "kycInboxContentWrap",
      smallBandColor: "#8994A0",
      titleColor: "#ffffff",
      right: (
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-light kycInboxBackBtn"
            onClick={() => nav("/clients")}
            style={{ whiteSpace: "nowrap" }}
            aria-label="Back to clients"
            title="Back to clients"
          >
            <img src={iconHomeA} alt="" className="kycInboxBackHomeIcon" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-light kycInboxLogoutBtn"
            onClick={handleLogout}
            style={{ whiteSpace: "nowrap" }}
          >
            Log out
          </button>
        </div>
      ),
    },
    [nav]
  );

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <motion.div
      variants={pageAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25 }}
      className="kycInboxPage staffShellContentPage"
    >
      <div className="kycInboxContentWrap py-4">
        <div className="searchcontainer">
          <div className="kycInboxControlRow">
            <div className="clientsToolbar kycInboxSearchSlot">
              <SearchBar
                value={search}
                onChange={setSearch}
                onClear={() => setSearch("")}
                placeholder="Search clients…"
                className="clientsSearch"
              />
            </div>

            <div className="kycInboxFilters">
              <div className="kycInboxFilterGroup">
                <span className="kycInboxFilterLabel">Type:</span>
                <button
                  type="button"
                  className={chipClass(typeFilter === "all")}
                  onClick={() => setTypeFilter("all")}
                >
                  All <span className="ms-1 opacity-75">({counts.total})</span>
                </button>
                <button
                  type="button"
                  className={chipClass(typeFilter === "kyc")}
                  onClick={() => setTypeFilter("kyc")}
                >
                  KYC{" "}
                  <span className="ms-1 opacity-75">
                    ({counts.kycPending + counts.kycAwaiting})
                  </span>
                </button>
                <button
                  type="button"
                  className={chipClass(typeFilter === "docs")}
                  onClick={() => setTypeFilter("docs")}
                >
                  Docs{" "}
                  <span className="ms-1 opacity-75">
                    ({counts.docsPending + counts.docsAwaiting})
                  </span>
                </button>
              </div>

              <div className="kycInboxFilterGroup">
                <span className="kycInboxFilterLabel">Status:</span>
                <button
                  type="button"
                  className={chipClass(statusFilter === "all")}
                  onClick={() => setStatusFilter("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={chipClass(statusFilter === "pending")}
                  onClick={() => setStatusFilter("pending")}
                >
                  Pending{" "}
                  <span className="ms-1 opacity-75">
                    ({counts.totalPending})
                  </span>
                </button>
                <button
                  type="button"
                  className={chipClass(statusFilter === "awaiting")}
                  onClick={() => setStatusFilter("awaiting")}
                >
                  Awaiting{" "}
                  <span className="ms-1 opacity-75">
                    ({counts.totalAwaiting})
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="firsttable_container pb-4">
          {err && <div className="alert alert-danger">{err}</div>}

          <div className="card shadow-sm kycInboxCard">
          <div className="p-3 border-bottom kycInboxCardHead">
            <div className="maintext">Unified Inbox</div>
            <div className="subtext">
              {(() => {
                const typeLabel =
                  typeFilter === "kyc" ? "KYC forms" :
                  typeFilter === "docs" ? "Documents" :
                  "KYC forms & documents";
                const statusLabel =
                  statusFilter === "pending" ? "Pending review" :
                  statusFilter === "awaiting" ? "Awaiting client response" :
                  "Pending reviews & awaiting responses";
                const count = `${filteredItems.length} item(s)`;
                return searchQ
                  ? `${filteredItems.length} result(s) matching your search`
                  : `${typeLabel} · ${statusLabel} · ${count}`;
              })()}
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle m-0 kycInboxTable">
              <thead className="kycInboxTableHead">
                <tr>
                  <th className="ps-3">Date</th>
                  <th>Client</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Details</th>
                  <th className="text-end pe-3">Action</th>
                </tr>
              </thead>

              <tbody>
                {(loading || invitesLoading) && unifiedItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-muted">
                      Loading…
                    </td>
                  </tr>
                )}

                {!loading && !invitesLoading && filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="noRowData p-4 text-muted">
                      {searchQ ? "No matching items." : "Inbox is empty."}
                    </td>
                  </tr>
                )}

                {filteredItems.map((it) => {
                  const dateTxt = fmtDate(it.dateTs);
                  const clickable = typeof it.rowOnClick === "function";

                  return (
                    <tr
                      key={it._key}
                      style={clickable ? { cursor: "pointer" } : undefined}
                      onClick={() => {
                        if (!clickable) return;
                        it.rowOnClick();
                      }}
                    >
                      <td className="ps-3 kycInboxDate">
                        <div className="small text-muted">{it.dateLabel}</div>
                        <div>{dateTxt || "—"}</div>
                      </td>

                      <td className="kycInboxName">
                        <div>{it.clientName || "—"}</div>
                        {/* {it.clientId ? (
                          <div className="small text-muted">{it.clientId}</div>
                        ) : null} */}
                      </td>

                      <td>{renderTypeBadge(it)}</td>

                      <td>{renderStatusPill(it)}</td>

                      <td>
                        {it.clientStatus ? (
                          <span className="badge badgeStatus">
                            <i
                              className={`statusIcon ${statusIconClass(
                                it.clientStatus
                              )}`}
                              aria-hidden="true"
                            />
                            <span className="ms-1">{it.clientStatus}</span>
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}

                        {/* KYC client status + type badge (when available) */}
                        {/* {it.type === "kyc" ? (
                          <div className="d-flex flex-column gap-1">
                            {it.clientType ? (
                              it.clientType === "individual" ? (
                                <span className="badge badgeType badgeTypeIndividual">
                                  <i
                                    className="bi bi-person me-1 typeIconIndividual"
                                    aria-hidden="true"
                                  />
                                  Individual
                                </span>
                              ) : it.clientType === "entity" ? (
                                <span className="badge badgeType badgeTypeEntity">
                                  <i
                                    className="bi bi-building me-1 typeIconEntity"
                                    aria-hidden="true"
                                  />
                                  Entity
                                </span>
                              ) : null
                            ) : null}

                            {it.clientStatus ? (
                              <span className="badge badgeStatus">
                                <i
                                  className={`statusIcon ${statusIconClass(
                                    it.clientStatus
                                  )}`}
                                  aria-hidden="true"
                                />
                                <span className="ms-1">
                                  {it.clientStatus || "—"}
                                </span>
                              </span>
                            ) : null}

                            {it.email ? (
                              <span className="small text-muted">
                                {it.email}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <div className="small text-muted">
                            {it.email ? it.email : "—"}
                          </div>
                        )} */}
                      </td>

                      <td className="text-end pe-3">
                        <div className="d-inline-flex align-items-center gap-2">
                          {it.onAction ? (
                            <button
                              type="button"
                              className="btn btn-sm kycInboxBtnReview"
                              disabled={!!it.actionDisabled}
                              onClick={(e) => {
                                e.stopPropagation();
                                it.onAction();
                              }}
                            >
                              {it.actionLabel || "Open"}
                            </button>
                          ) : (
                            <span />
                          )}
                          <button
                            type="button"
                            className="btn btn-sm kycInboxBtnDeleteIcon"
                            onClick={(e) => { e.stopPropagation(); confirmDeleteRow(it); }}
                            aria-label="Delete"
                            title="Delete"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="kycInboxIcon"
                              viewBox="0 0 16 16"
                              aria-hidden="true"
                              fill="currentColor"
                            >
                              <path d="M2.037 3.225A.7.7 0 0 1 2 3c0-1.105 2.686-2 6-2s6 .895 6 2a.7.7 0 0 1-.037.225l-1.684 10.104A2 2 0 0 1 10.305 15H5.694a2 2 0 0 1-1.973-1.671zm9.89-.69C10.966 2.214 9.578 2 8 2c-1.58 0-2.968.215-3.926.534-.477.16-.795.327-.975.466.18.14.498.307.975.466C5.032 3.786 6.42 4 8 4s2.967-.215 3.926-.534c.477-.16.795-.327.975-.466-.18-.14-.498-.307-.975-.466z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* optional footer */}
          {/* <div className="p-3 border-top small text-muted">
            Tip: use filters to focus on “Pending” (work queue) vs “Awaiting”
            (follow-ups).
          </div> */}
        </div>
        </div>
      </div>

      <Toast ref={toastRef} />
      <Tooltip id="inboxReminderTip" place="top" style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} />

      <AnimatePresence>
        {kycReviewSubId && (
          <motion.div
            className="docsReviewModalOverlay kycReviewModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onMouseDown={() => setKycReviewSubId(null)}
          >
            <motion.div
              className="docsReviewModalShell kycReviewModalShell"
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="docsReviewModalHeader">
                <div className="docsReviewModalHeaderTitle">
                  KYC Review
                  {kycReviewClientName && (
                    <span className="docsReviewModalHeaderClient"> · {kycReviewClientName}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="docsReviewModalCloseBtn"
                  onClick={() => setKycReviewSubId(null)}
                  aria-label="Close"
                  title="Close"
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>
              <div className="docsReviewModalBody">
                <KycReviewPage
                  key={kycReviewSubId}
                  submissionId={kycReviewSubId}
                  isModal={true}
                  onClose={() => setKycReviewSubId(null)}
                  onSuccess={(toast) => {
                    setKycReviewSubId(null);
                    toastRef.current?.show(toast);
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {reviewDocId && (
          <motion.div
            className="docsReviewModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onMouseDown={() => setReviewDocId(null)}
          >
            <motion.div
              className="docsReviewModalShell"
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className={`docsReviewModalHeader${innerDocModalOpen ? " docsReviewModalHeaderBlurred" : ""}`}>
                <div className="docsReviewModalHeaderTitle">
                  Documents Review
                  {reviewClientName && (
                    <span className="docsReviewModalHeaderClient"> · {reviewClientName}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="docsReviewModalCloseBtn"
                  onClick={() => setReviewDocId(null)}
                  aria-label="Close"
                  title="Close"
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>
              <div className="docsReviewModalBody">
                <StaffDocumentsReviewPage
                  key={reviewDocId}
                  docId={reviewDocId}
                  onClose={() => { setReviewDocId(null); setInnerDocModalOpen(false); }}
                  isModal={true}
                  onInnerModalChange={setInnerDocModalOpen}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
